import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Card,
  CardBody,
  Badge,
  Divider,
  Box,
  Spinner,
  Center,
  useToast
} from '@chakra-ui/react'
import { observer } from 'mobx-react'
import { getReleaseNotes } from 'API/releaseNote'
import { useStores } from 'hooks/useStores'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMemberPermissionId } from 'interfaces/group'
import config from 'configs'
import ReleaseNotesModal from '../ReleaseNotesModal'

interface ReleaseNote {
  id?: number
  version: string
  title: string
  content: string
  releaseDate: string
  published: boolean
  sortOrder: number
}

interface CurrentVersionModalProps {
  isOpen: boolean
  onClose: () => void
}

const CurrentVersionModal: React.FC<CurrentVersionModalProps> = ({ isOpen, onClose }) => {
  const [currentVersionNote, setCurrentVersionNote] = useState<ReleaseNote | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showReleaseNotesModal, setShowReleaseNotesModal] = useState(false)
  const toast = useToast()

  useEffect(() => {
    if (isOpen) {
      fetchCurrentVersionNote()
    }
  }, [isOpen])

  const fetchCurrentVersionNote = async () => {
    try {
      setIsLoading(true)
      const notes = await getReleaseNotes()

      // Find the release note that matches the current app version
      const currentNote = notes.find(note => note.version === config.version)
      setCurrentVersionNote(currentNote || null)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load release note for current version',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsLoading(false)
    }
  }

  const formatReleaseContent = (content: string) => {
    return content.split('\n').map((line, index) => (
      <Text key={index} mb={2}>
        {line}
      </Text>
    ))
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>App Version {config.version}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoading ? (
              <Center py={8}>
                <Spinner size="lg" />
              </Center>
            ) : currentVersionNote ? (
              <VStack spacing={4} align="stretch">
                <Card shadow="sm" bg="blue.50">
                  <CardBody>
                    <VStack align="start" spacing={3}>
                      <Badge colorScheme="blue" fontSize="sm">
                        ✅ Released in v{currentVersionNote.version}
                      </Badge>
                      <Text fontSize="lg" fontWeight="bold">
                        {currentVersionNote.title}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Released on {new Date(currentVersionNote.releaseDate).toLocaleDateString()}
                      </Text>
                    </VStack>
                    <Divider my={4} />
                    <Box fontSize="sm" lineHeight="1.6" color="gray.700">
                      {formatReleaseContent(currentVersionNote.content)}
                    </Box>
                  </CardBody>
                </Card>
              </VStack>
            ) : (
              <Card>
                <CardBody textAlign="center" py={8}>
                  <VStack spacing={3}>
                    <Text fontSize="lg" color="gray.500">
                      No release notes found for version {config.version}
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                      This version may not have published release notes yet, or you may need to check with your
                      administrator.
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {/* Admin controls */}
            <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
              <Button
                colorScheme="blue"
                variant="outline"
                size="sm"
                onClick={() => setShowReleaseNotesModal(true)}
                width="full"
              >
                View Release Notes
              </Button>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Release Notes Management Modal */}
      <ReleaseNotesModal isOpen={showReleaseNotesModal} onClose={() => setShowReleaseNotesModal(false)} />
    </>
  )
}

export default CurrentVersionModal
